import Typography from "@material-ui/core/Typography";
import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import AdGroupsCreativesDashboard from "./components/AdGroupsCreativesDashboard/AdGroupsCreativesDashboard";
import Button from "../../../components/elements/Button";
import CampaignLineItemsReportDashboard from "./components/LineItemsReportSectionComponents/CampaignLineItemsReportDashboard";
import CampaignReportDashboard from "./components/CampaignReportSectionComponents/CampaignReportDashboard";
import CampaignReportDashboardToolbar from "./components/CampaignReportSectionComponents/CampaignReportDashboardToolbar";
import CampaignTargetingSelectionsDashboard from "./components/CampaignTargetingSelectionsComponents/CampaignTargetingSelectionsDashboard";
import Loader from "../../../components/elements/Loader";
import classes from "./CampaignsDashboard.module.scss";
import { CamapignReportType, CampaignType, ReportTimeFrames } from "../../../types/campaignTypes";
import { SelectOptionType } from "../../../components/elements/Select/Select";
import { getQueryParams } from "../../../helpers/getQueryParams";

type CampaignsDashboardProps = {
  campaigns: CampaignType[];
  selectedTimeFrame: ReportTimeFrames;
  handleTimeFrameChange: (e: any) => void;
  fetchAndSetCampaigns: () => void;
  handleOpenCampaignsDialog: () => void;
  selectedCampaign: CampaignType | null;
  setSelectedCampaign: React.Dispatch<React.SetStateAction<CampaignType | null>>;
  selectedCampaignReport: CamapignReportType | null;
  customReportDate: Date | undefined;
  isFetching: boolean;
  isCustomDateChanged: boolean;
};

const CampaignsDashboard: FC<CampaignsDashboardProps> = ({
  campaigns,
  selectedTimeFrame,
  handleTimeFrameChange,
  fetchAndSetCampaigns,
  handleOpenCampaignsDialog,
  selectedCampaign,
  setSelectedCampaign,
  selectedCampaignReport,
  customReportDate,
  isFetching,
  isCustomDateChanged,
}) => {
  const history = useHistory();
  const location = useLocation();

  const campaignOptions: SelectOptionType[] = useMemo(
    () =>
      campaigns.map(({ name, brandgenCampaignId }) => ({
        label: name,
        value: brandgenCampaignId,
      })),
    [campaigns],
  );

  const handleChangeSelectedCampaign = useCallback(
    (value: SelectOptionType | null) => {
      if (value) {
        const brandgenCampaignId = value.value as string;
        setSelectedCampaign(campaigns.find((campaign) => campaign.brandgenCampaignId === brandgenCampaignId)!);
        history.push({
          pathname: location.pathname,
          search: `?campaignId=${brandgenCampaignId}`,
        });
      }
    },
    [campaigns, history, location.pathname, setSelectedCampaign],
  );

  useEffect(() => {
    const queryParams = getQueryParams();
    const queryCampaignId = queryParams?.campaignId;
    if (!queryCampaignId) {
      setSelectedCampaign(campaigns[0]);
      history.push({
        pathname: location.pathname,
        search: `?campaignId=${campaigns[0].brandgenCampaignId}`,
      });
    } else {
      const foundCampaign = campaigns.find((campaign) => campaign.brandgenCampaignId === queryCampaignId);
      if (foundCampaign) {
        setSelectedCampaign(foundCampaign);
      } else {
        setSelectedCampaign(campaigns[0]);
      }
    }
  }, [campaigns, history, location.pathname, setSelectedCampaign]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h2">Campaigns Dashboard</Typography>
        <Button variant="outlined" onClick={handleOpenCampaignsDialog}>
          View All Campaigns
        </Button>
      </div>
      {!selectedCampaign ? (
        <Loader />
      ) : (
        <>
          <div className={classes.campaignReportSection}>
            <CampaignReportDashboardToolbar
              selectedTimeFrame={selectedTimeFrame}
              handleTimeFrameChange={handleTimeFrameChange}
              selectedCampaign={selectedCampaign}
              campaignOptions={campaignOptions}
              handleChangeSelectedCampaign={handleChangeSelectedCampaign}
              fetchAndSetCampaigns={fetchAndSetCampaigns}
            />
            {!isFetching && (
              <CampaignReportDashboard
                campaignReport={selectedCampaignReport}
                campaign={selectedCampaign}
                selectedTimeFrame={selectedTimeFrame}
              />
            )}
          </div>

          <div className={classes.campaignsLineItemsReportSection}>
            <CampaignLineItemsReportDashboard
              selectedTimeFrame={selectedTimeFrame}
              campaign={selectedCampaign}
              customReportDate={customReportDate}
              isCustomDateChanged={isCustomDateChanged}
            />
          </div>

          <div className={classes.campaignsTargetingInfoSection}>
            <CampaignTargetingSelectionsDashboard campaign={selectedCampaign} />
          </div>

          <div className={classes.adGroupsDashboard}>
            <AdGroupsCreativesDashboard
              selectedTimeFrame={selectedTimeFrame}
              selectedCampaign={selectedCampaign}
              customReportDate={customReportDate}
              isCustomDateChanged={isCustomDateChanged}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CampaignsDashboard;
